import {State} from 'centrifuge';
import {createContext, PropsWithChildren, useCallback, useContext, useMemo, useRef, useState} from 'react';
import {v4 as uuidv4} from 'uuid';

import {CentrifugeContext} from '../../../provider/centrifuge/centrifuge-context';
import {useCentrifugeSubscription} from '../../../provider/centrifuge/centrifuge-hook';
import {useUser} from '../../../provider/user/user-hook';

import {
    canReceiveReviewRealtime,
    canSendReviewRealtime,
    hasAnyReviewRealtimeReceiveBrand,
} from './review-realtime-feature-helper';
import {getReviewLock, isReviewLockedByOther} from './review-realtime-lock-store';
import {
    buildPresencePublicationPayload,
    buildPresenceUpdatePayload,
    getBrandPresenceChannelName,
} from './review-realtime-presence-helper';
import {
    PublishReviewUpdateType,
    ReviewLockType,
    ReviewPresenceActivityOptionsType,
    ReviewPresenceEventEnum,
    ReviewPresenceUpdatePublicationType,
    ReviewRealtimeLockMapType,
} from './review-realtime-presence-type';
import {useReviewRealtimeConfig} from './use-review-realtime-config-hook';
import {useReviewRealtimePresenceRuntimeHook} from './use-review-realtime-presence-runtime-hook';

type ReviewRealtimePresenceContextType = {
    isReady: boolean;
    locksByReviewId: ReviewRealtimeLockMapType;
    getLockForReview: (reviewId: number) => ReviewLockType | null;
    isLockedByOther: (reviewId: number) => boolean;
    publishReviewUpdate: PublishReviewUpdateType;
    startActivity: (options: ReviewPresenceActivityOptionsType) => void;
    stopActivity: (options: ReviewPresenceActivityOptionsType) => void;
};

const ReviewRealtimePresenceContext = createContext<ReviewRealtimePresenceContextType | null>(null);

type ReviewRealtimePresenceProviderPropsType = PropsWithChildren<{
    onReviewUpdate?: (payload: ReviewPresenceUpdatePublicationType) => void;
}>;

// eslint-disable-next-line max-statements
export function ReviewRealtimePresenceProvider(props: ReviewRealtimePresenceProviderPropsType): JSX.Element {
    const {children, onReviewUpdate} = props;

    const centrifuge = useContext(CentrifugeContext);
    const {publishToChannel} = useCentrifugeSubscription();
    const {user} = useUser();
    const reviewRealtimeConfig = useReviewRealtimeConfig();
    const sessionIdRef = useRef<string>(uuidv4());
    const activeActivitiesRef = useRef<Array<ReviewPresenceActivityOptionsType>>([]);
    const [locksByReviewId, setLocksByReviewId] = useState<ReviewRealtimeLockMapType>({});

    const canReceiveAny = hasAnyReviewRealtimeReceiveBrand(user, reviewRealtimeConfig);
    const centrifugeState = centrifuge?.state;
    const isReady = Boolean(canReceiveAny && centrifugeState === State.Connected);

    const canReceiveForBrand = useCallback(
        (brandId: number) => canReceiveReviewRealtime(user, brandId, reviewRealtimeConfig),
        [reviewRealtimeConfig, user]
    );

    const canSendForBrand = useCallback(
        (brandId: number) => canSendReviewRealtime(user, brandId, reviewRealtimeConfig),
        [reviewRealtimeConfig, user]
    );

    const publishPresenceEvent = useCallback(
        async (
            options: ReviewPresenceActivityOptionsType & {
                event:
                    | ReviewPresenceEventEnum.Lock
                    | ReviewPresenceEventEnum.Unlock
                    | ReviewPresenceEventEnum.Heartbeat;
            }
        ) => {
            if (!canSendForBrand(options.brandId) || !user || !centrifuge) {
                return;
            }

            const {state} = centrifuge;

            if (state !== State.Connected) {
                return;
            }

            const channel = getBrandPresenceChannelName(options.brandId);
            const payload = buildPresencePublicationPayload({
                options,
                sessionId: sessionIdRef.current,
                userId: user.id,
            });

            await publishToChannel(channel, payload);
        },
        [canSendForBrand, centrifuge, publishToChannel, user]
    );

    const publishReviewUpdate = useCallback<PublishReviewUpdateType>(
        (reviewId, brandId, changes) => {
            if (!canSendForBrand(brandId) || !user || !centrifuge) {
                return;
            }

            const {state} = centrifuge;

            if (state !== State.Connected) {
                return;
            }

            const channel = getBrandPresenceChannelName(brandId);
            const payload = buildPresenceUpdatePayload({
                reviewId,
                brandId,
                sessionId: sessionIdRef.current,
                userId: user.id,
                changes,
            });

            publishToChannel(channel, payload).catch(() => {
                /* ignore publish errors */
            });
        },
        [canSendForBrand, centrifuge, publishToChannel, user]
    );

    const removeActiveActivity = useCallback((options: ReviewPresenceActivityOptionsType) => {
        activeActivitiesRef.current = activeActivitiesRef.current.filter(
            (activity) =>
                !(
                    activity.reviewId === options.reviewId &&
                    activity.brandId === options.brandId &&
                    activity.action === options.action
                )
        );
    }, []);

    const addActiveActivity = useCallback((options: ReviewPresenceActivityOptionsType) => {
        const isAlreadyActive = activeActivitiesRef.current.some(
            (activity) =>
                activity.reviewId === options.reviewId &&
                activity.brandId === options.brandId &&
                activity.action === options.action
        );

        if (!isAlreadyActive) {
            activeActivitiesRef.current = [...activeActivitiesRef.current, options];
        }
    }, []);

    const stopAllActivities = useCallback(() => {
        const activities = [...activeActivitiesRef.current];

        activeActivitiesRef.current = [];

        activities.forEach((activity) => {
            publishPresenceEvent({...activity, event: ReviewPresenceEventEnum.Unlock}).catch(() => {
                /* ignore publish errors */
            });
        });
    }, [publishPresenceEvent]);

    const startActivity = useCallback(
        (options: ReviewPresenceActivityOptionsType) => {
            if (!canSendForBrand(options.brandId)) {
                return;
            }

            const isAlreadyActive = activeActivitiesRef.current.some(
                (activity) =>
                    activity.reviewId === options.reviewId &&
                    activity.brandId === options.brandId &&
                    activity.action === options.action
            );

            addActiveActivity(options);

            if (isAlreadyActive) {
                return;
            }

            publishPresenceEvent({...options, event: ReviewPresenceEventEnum.Lock}).catch(() => {
                /* ignore publish errors */
            });
        },
        [addActiveActivity, canSendForBrand, publishPresenceEvent]
    );

    const stopActivity = useCallback(
        (options: ReviewPresenceActivityOptionsType) => {
            const wasActive = activeActivitiesRef.current.some(
                (activity) =>
                    activity.reviewId === options.reviewId &&
                    activity.brandId === options.brandId &&
                    activity.action === options.action
            );

            if (!wasActive) {
                return;
            }

            removeActiveActivity(options);

            setLocksByReviewId((previousLocks) => {
                const existingLock = previousLocks[options.reviewId];

                if (
                    existingLock?.userId === user?.id &&
                    existingLock?.sessionId === sessionIdRef.current &&
                    existingLock?.action === options.action
                ) {
                    const {[options.reviewId]: _unusedLock, ...restLocks} = previousLocks;

                    return restLocks;
                }

                return previousLocks;
            });

            if (!canSendForBrand(options.brandId)) {
                return;
            }

            publishPresenceEvent({...options, event: ReviewPresenceEventEnum.Unlock}).catch(() => {
                /* ignore publish errors */
            });
        },
        [canSendForBrand, publishPresenceEvent, removeActiveActivity, user?.id]
    );

    useReviewRealtimePresenceRuntimeHook({
        activeActivitiesRef,
        canReceiveAny,
        canReceiveForBrand,
        canSendForBrand,
        centrifuge,
        onReviewUpdate,
        publishPresenceEvent,
        sessionIdRef,
        setLocksByReviewId,
        stopAllActivities,
    });

    const getLockForReview = useCallback(
        (reviewId: number) => {
            return getReviewLock(locksByReviewId, reviewId);
        },
        [locksByReviewId]
    );

    const isLockedByOther = useCallback(
        (reviewId: number) => {
            if (!user) {
                return false;
            }

            const lock = locksByReviewId[reviewId];

            if (!lock || !canReceiveForBrand(lock.brandId)) {
                return false;
            }

            return isReviewLockedByOther(locksByReviewId, reviewId, user.id, sessionIdRef.current);
        },
        [canReceiveForBrand, locksByReviewId, user]
    );

    const contextValue = useMemo(
        (): ReviewRealtimePresenceContextType => ({
            isReady,
            locksByReviewId,
            getLockForReview,
            isLockedByOther,
            publishReviewUpdate,
            startActivity,
            stopActivity,
        }),
        [getLockForReview, isLockedByOther, isReady, locksByReviewId, publishReviewUpdate, startActivity, stopActivity]
    );

    return (
        <ReviewRealtimePresenceContext.Provider value={contextValue}>{children}</ReviewRealtimePresenceContext.Provider>
    );
}

export function useReviewRealtimePresence(): ReviewRealtimePresenceContextType {
    const context = useContext(ReviewRealtimePresenceContext);

    if (!context) {
        throw new Error('useReviewRealtimePresence must be used within ReviewRealtimePresenceProvider');
    }

    return context;
}
